import { FormControl } from '@angular/forms';
import { createValidator } from '@ps-gui/ui';
import {
  FieldType,
  randomDigits,
  randomInt,
  randomOf,
  randomPhoneNumber,
  randomWord,
} from '@ps-gui/shared-utils';
import { ExtractedValue, ExtractedValuesByKey } from '../../models/extracted-values';
import { FieldModel } from '../../models/extraction-models/extraction-models';
import { addDays, formatDate, randomEmailAddress, randomZipCode } from './random';

export type FillMode = 'required' | 'all';

const MAX_DAYS_BACK = 900;

const randomDate = () => formatDate(addDays(new Date(), -randomInt(1, MAX_DAYS_BACK)));

const randomTime = () =>
  [randomInt(0, 23), randomInt(0, 59)].map((part) => String(part).padStart(2, '0')).join(':');

const BY_ERROR_KEY: Record<string, () => ExtractedValue> = {
  invalidZip: randomZipCode,
  invalidEmail: randomEmailAddress,
  invalidDate: randomDate,
  invalidTime: randomTime,
};

const TITEL = ['Dr.', 'Dr. med.', 'Prof. Dr. med.'];

/** All in ps-backend's ICD10Codes.xml - any other code arrives as "ICD-10 Code unbekannt". */
const ICD10_CODES = ['S13.4', 'S06.0', 'S82.6', 'S52.50', 'S83.53', 'S20.2'];

const OPS_CODES = ['3-200', '3-203', '3-802', '3-806', '5-794.k6', '5-813.4'];

/** Neither the field type nor a validator provides any clues—the key is the only remaining clue. */
const BY_KEY: [keyPart: string, produce: () => ExtractedValue][] = [
  ['schadennummer', () => `${randomInt(1, 9)}${randomDigits(6)}`],
  ['kostentraegerzeichen', () => randomWord(3, 3).toUpperCase()],
  // Not a bare 'hausnummer' - the ese and attest masks hold street and number in one field.
  ['kostentraeger_hausnummer', () => `${randomInt(10, 99)}`],
  ['vorname', () => randomWord(6, 9)],
  ['nachname', () => randomWord(8, 13)],
  ['titel', () => randomOf(TITEL)],
  ['icd10_code', () => randomOf(ICD10_CODES)],
  ['ops_code', () => randomOf(OPS_CODES)],
  ['email', randomEmailAddress],
  ['telefon', randomPhoneNumber],
  ['fax', randomPhoneNumber],
];

const byType = (fieldModel: FieldModel): ExtractedValue => {
  switch (fieldModel.type) {
    case FieldType.DATE:
      return randomDate();
    case FieldType.TIME:
      return randomTime();
    case FieldType.NUMBER:
      return randomInt(1, 999);
    case FieldType.CURRENCY:
      return randomInt(500, 500_000) / 100;
    case FieldType.TEXTAREA:
      return `${randomWord(6, 9)} ${randomWord(8, 13)} ${randomWord(6, 12)}.`;
    default:
      return randomWord(6, 13);
  }
};

/**
 * `Validations.maxLength(n)` keeps `n` inside its closure, so asking the validator is the only
 * way to the limit - shorten until it stops complaining.
 */
const trimToMaxLength = (value: ExtractedValue, fieldModel: FieldModel): ExtractedValue => {
  if (typeof value !== 'string') return value;

  const validators = (fieldModel.validators ?? []).map(createValidator);
  const isTooLong = (candidate: string) =>
    validators.some((validator) => validator(new FormControl(candidate))?.['maxLength']);

  let candidate = value;
  while (candidate.length > 1 && isTooLong(candidate)) {
    candidate = candidate.slice(0, -1);
  }

  return candidate;
};

const isRequired = (fieldModel: FieldModel) =>
  (fieldModel.validators ?? []).some((validation) => validation.errorKey === 'required');

/**
 * Strictest constraint first: the options the field offers, then a validator's error key, then
 * the format hint in the key, and the field type as a fallback - a value the field rejects is
 * worthless as test data.
 */
export const generateFieldValue = (fieldModel: FieldModel): ExtractedValue => {
  if ('options' in fieldModel) {
    const option = randomOf<string | { label: string; value: string }>(fieldModel.options);
    return typeof option === 'string' ? option : option.value;
  }

  const byErrorKey = (fieldModel.validators ?? [])
    .map((validation) => BY_ERROR_KEY[validation.errorKey])
    .find(Boolean);

  const key = fieldModel.key.toLowerCase();
  const byKey = BY_KEY.find(([keyPart]) => key.includes(keyPart))?.[1];

  return trimToMaxLength((byErrorKey ?? byKey)?.() ?? byType(fieldModel), fieldModel);
};

export const generateValues = (
  fieldModels: readonly FieldModel[],
  mode: FillMode,
): ExtractedValuesByKey =>
  Object.fromEntries(
    fieldModels
      .filter((fieldModel) => mode === 'all' || isRequired(fieldModel))
      .map((fieldModel) => [fieldModel.key, generateFieldValue(fieldModel)]),
  );
